import { timeoutMessage } from '../utils/timeoutMessage';
import React, {
    Children,
    forwardRef,
    isValidElement,
    useImperativeHandle,
    useMemo,
    useRef,
    useState,
    useEffect,
} from 'react';
import './BootstrapControls.css';
import LocalizedDateInput from './LocalizedDateInput';
import { SABIT_LKP_EVENT } from '../utils/sabitLkp';
import { currentSabitItems, detectSabitStTur, isGeneralYetkiUser, openSabitDefinitions } from '../utils/sabitDefinitionAccess';

function resolveExpr(item, expr) {
    if (typeof expr === 'function') return expr(item);
    return expr ? item?.[expr] : item;
}

function normalizeType(type) {
    if (type === 'success') return 'success';
    if (type === 'danger') return 'danger';
    if (type === 'default') return 'primary';
    return 'secondary';
}

function iconText(icon) {
    const icons = {
        save: '💾', trash: '🗑', remove: '−', plus: '+', add: '+', edit: '✎',
        close: '×', refresh: '↻', print: '🖨', download: '↓', photo: '▣',
        check: '✓', event: '▣', todo: '☑', tableproperties: '▦', columnfield: '▥',
    };
    return icons[icon] || '';
}

export const Button = forwardRef(function Button({
    text,
    icon,
    type = 'normal',
    stylingMode = 'contained',
    onClick,
    disabled,
    visible = true,
    width,
    height,
    hint,
    className = '',
    children,
}, ref) {
    const elementRef = useRef(null);
    const clickLockedRef = useRef(false);
    const [clickBusy, setClickBusy] = useState(false);
    const isSave = icon === 'save' || /kaydet|save/i.test(String(text || hint || children || ''));
    const handleClick = (event) => {
        if (disabled || clickLockedRef.current) return;
        if (!isSave) return onClick?.({ event, component: instance });
        clickLockedRef.current = true;
        setClickBusy(true);
        const release = () => {
            clickLockedRef.current = false;
            setClickBusy(false);
        };
        const cooldown = new Promise(resolve => setTimeout(resolve, 500));
        try {
            const result = onClick?.({ event, component: instance });
            // Also cover synchronous handlers that start a request without returning it.
            Promise.allSettled([cooldown, Promise.resolve(result)]).then(release);
        } catch (error) {
            cooldown.then(release);
            throw error;
        }
    };
    const instance = useMemo(() => ({
        option: (name) => name === 'disabled' ? disabled || clickBusy : undefined,
        focus: () => elementRef.current?.focus(),
    }), [disabled, clickBusy]);
    useImperativeHandle(ref, () => ({ instance }), [instance]);
    if (!visible) return null;
    const variant = normalizeType(type);
    const outline = stylingMode === 'outlined' ? 'outline-' : '';
    return (
        <button ref={elementRef} type="button" className={`btn btn-sm btn-${outline}${variant} bootstrap-control-button ${className}`}
            disabled={disabled || clickBusy} aria-busy={clickBusy || undefined} style={{ width, height }} title={hint}
            onClick={handleClick}>
            {clickBusy ? <><span className="spinner-border spinner-border-sm me-1" aria-hidden="true" />Kaydediliyor…</>
                : <>{icon && <span aria-hidden="true">{iconText(icon)} </span>}{text || children}</>}
        </button>
    );
});

export default Button;

function valueComponent(inputType) {
    return forwardRef(function ValueComponent({
        value,
        defaultValue,
        onValueChanged,
        readOnly,
        disabled,
        width,
        height,
        className = '',
        placeholder,
        maxLength,
        min,
        max,
        type,
        displayFormat,
        mode,
        showClearButton,
        items = [],
        dataSource,
        displayExpr,
        valueExpr,
        text,
        onEnterKey,
        onFocusOut,
    }, ref) {
        const [internalValue, setInternalValue] = useState(defaultValue ?? value ?? '');
        const [, setSabitRevision] = useState(0);
        const controlled = value !== undefined;
        const currentValue = controlled ? value : internalValue;
        const elementRef = useRef(null);
        const instance = useMemo(() => ({
            option: (name, nextValue) => {
                if (name !== 'value') return undefined;
                if (nextValue === undefined) return currentValue;
                setInternalValue(nextValue);
                return nextValue;
            },
            focus: () => elementRef.current?.focus(),
        }), [currentValue]);
        useImperativeHandle(ref, () => ({ instance }), [instance]);
        useEffect(() => {
            const refresh = () => setSabitRevision((revision) => revision + 1);
            window.addEventListener(SABIT_LKP_EVENT, refresh);
            return () => window.removeEventListener(SABIT_LKP_EVENT, refresh);
        }, []);
        const commit = (nextValue) => {
            if (!controlled) setInternalValue(nextValue);
            onValueChanged?.({ value: nextValue, previousValue: currentValue, component: instance });
        };
        const common = {
            ref: elementRef,
            disabled,
            readOnly,
            className: `${inputType === 'checkbox' ? 'form-check-input' : 'form-control form-control-sm'} ${className}`,
            style: { width, height },
        };
        if (inputType === 'select') {
            const configuredSource = Array.isArray(dataSource) ? dataSource : items;
            const stTur = detectSabitStTur(configuredSource);
            const source = stTur ? currentSabitItems(stTur) : configuredSource;
            return (
                <div className="d-flex align-items-stretch" style={{ width }}>
                    <select {...common} className={`form-select form-select-sm ${className}`} value={currentValue ?? ''}
                        style={{ flex: 1, minWidth: 0 }}
                        onChange={(event) => {
                            const item = source.find((entry) => String(resolveExpr(entry, valueExpr)) === String(event.target.value));
                            commit(item ? resolveExpr(item, valueExpr) : null);
                        }}>
                        {(showClearButton || currentValue == null || currentValue === '') && <option value="">{placeholder || 'Seçiniz'}</option>}
                        {source.map((item, index) => {
                            const itemValue = resolveExpr(item, valueExpr);
                            return <option key={`${itemValue}-${index}`} value={itemValue ?? ''} disabled={item?.disabled}>{resolveExpr(item, displayExpr)}</option>;
                        })}
                    </select>
                    {stTur && isGeneralYetkiUser() && <button type="button"
                        title="Sabit tanım ekranı" aria-label="Sabit tanım ekranı"
                        style={{ width: 28, minWidth: 28, padding: 0, marginLeft: 3, background: '#28a745', color: '#000', border: '1px solid #1e7e34', borderRadius: 4, fontWeight: 700 }}
                        onClick={() => openSabitDefinitions(stTur)}>T</button>}
                </div>
            );
        }
        if (inputType === 'checkbox') {
            return <label className="form-check d-inline-flex align-items-center gap-2"><input {...common} type="checkbox"
                checked={Boolean(currentValue)} onChange={(event) => commit(event.target.checked)} />{text}</label>;
        }
        const withTime = type === 'datetime' || String(displayFormat || '').includes('HH');
        if (inputType === 'date') {
            return (
                <LocalizedDateInput
                    {...common}
                    value={currentValue}
                    withTime={withTime}
                    placeholder={placeholder}
                    min={min}
                    max={max}
                    onValueChange={commit}
                    onKeyDown={(event) => event.key === 'Enter' && onEnterKey?.({ event, component: instance })}
                    onBlur={(event) => onFocusOut?.({ event, component: instance })}
                />
            );
        }
        return <input {...common} type={mode === 'password' ? 'password' : inputType} value={currentValue ?? ''}
            placeholder={placeholder} maxLength={maxLength} min={min} max={max}
            onKeyDown={(event) => event.key === 'Enter' && onEnterKey?.({ event, component: instance })}
            onBlur={(event) => onFocusOut?.({ event, component: instance })}
            onChange={(event) => {
                let nextValue = event.target.value;
                if (inputType === 'number') nextValue = nextValue === '' ? null : Number(nextValue);
                commit(nextValue);
            }} />;
    });
}

export const TextBox = valueComponent('text');
export const NumberBox = valueComponent('number');
export const DateBox = valueComponent('date');
export const SelectBox = valueComponent('select');
export const CheckBox = valueComponent('checkbox');

export function ScrollView({ children, width, height, className = '', style = {} }) {
    return <div className={`bootstrap-scroll-view ${className}`} style={{ width, height, overflow: 'auto', ...style }}>{children}</div>;
}

export const Position = () => null;

export function Popup({
    visible,
    onHiding,
    children,
    title,
    showTitle = true,
    showCloseButton = true,
    width,
    height,
    fullScreen,
    className = '',
    toolbarItems = [],
}) {
    if (!visible) return null;
    const content = Children.toArray(children).filter((child) => !(isValidElement(child) && child.type === Position));
    const titleItems = toolbarItems.filter((item) => item.widget === 'dxTextBox');
    const actionItems = toolbarItems.filter((item) => item.widget === 'dxButton');
    return (
        <div className={`bootstrap-popup-backdrop ${className}`} role="presentation">
            <div className="bootstrap-popup" role="dialog" aria-modal="true" aria-label={title}
                style={{ width: fullScreen ? '100%' : width, height: fullScreen ? '100%' : height }}>
                {showTitle && (
                    <div className="bootstrap-popup__header">
                        <strong>{title || titleItems.map((item) => item.options?.text).filter(Boolean).join(' ')}</strong>
                        {showCloseButton && <button type="button" className="btn-close" aria-label="Kapat" onClick={() => onHiding?.()} />}
                    </div>
                )}
                <div className="bootstrap-popup__body">{content}</div>
                {actionItems.length > 0 && (
                    <div className="bootstrap-popup__footer">
                        {actionItems.map((item, index) => (
                            <Button key={index} {...item.options} />
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}

export const BoxItem = ({ children }) => <>{children}</>;

export function Box({ children, direction = 'row', width, height, className = '' }) {
    return (
        <div className={`bootstrap-box ${className}`} style={{ flexDirection: direction, width, height }}>
            {Children.map(children, (child) => {
                if (!isValidElement(child)) return child;
                return <div style={{ flex: child.props.ratio || 1, minWidth: 0 }}>{child.props.children}</div>;
            })}
        </div>
    );
}

export function RadioGroup({ items = [], value, onValueChanged, displayExpr, valueExpr, layout = 'horizontal', itemRender }) {
    const name = useRef(`radio-${Math.random().toString(36).slice(2)}`);
    return (
        <div className={`bootstrap-radio-group bootstrap-radio-group--${layout}`}>
            {items.map((item, index) => {
                const itemValue = resolveExpr(item, valueExpr);
                return (
                    <label className="form-check" key={`${itemValue}-${index}`}>
                        <input className="form-check-input" type="radio" name={name.current} disabled={item?.disabled}
                            checked={String(itemValue) === String(value)} onChange={() => onValueChanged?.({ value: itemValue })} />
                        <span className="form-check-label">{itemRender ? itemRender(item) : resolveExpr(item, displayExpr)}</span>
                    </label>
                );
            })}
        </div>
    );
}

export function FileUploader({
    uploadUrl,
    uploadCustomData = {},
    multiple,
    accept,
    disabled,
    onUploaded,
    onUploadError,
}) {
    const [uploading, setUploading] = useState(false);
    const upload = async (files) => {
        setUploading(true);
        try {
            for (const file of files) {
                const body = new FormData();
                body.append('file', file);
                Object.entries(uploadCustomData).forEach(([key, value]) => body.append(key, value == null ? '' : value));
                const response = await fetch(uploadUrl, { method: 'POST', body, credentials: 'include' });
                if (!response.ok) throw new Error(response.status === 504 ? timeoutMessage() : `Upload ${response.status}`);
                onUploaded?.({ file, request: response });
            }
        } catch (error) {
            onUploadError?.({ error });
        } finally {
            setUploading(false);
        }
    };
    return (
        <label className={`btn btn-sm btn-outline-primary bootstrap-file-uploader ${disabled ? 'disabled' : ''}`}>
            {uploading ? 'Yükleniyor…' : 'Dosya seç'}
            <input type="file" hidden multiple={multiple} accept={accept} disabled={disabled || uploading}
                onChange={(event) => void upload(Array.from(event.target.files || []))} />
        </label>
    );
}
